/**
 * StripeCustomers.jsx – Phase 1a
 * Kundenliste mit Stripe-Daten (Read-Only)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Search, Loader2, Users, CreditCard, AlertTriangle,
  CheckCircle2, Clock, ChevronRight, RefreshCw, XCircle,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import StripeFinanceActions from './StripeFinanceActions';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function StatusBadge({ status, statusDe }) {
  const color = {
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    trialing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    past_due: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    canceled: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    complimentary: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    unpaid: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  }[status] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`}>{statusDe || status || '–'}</span>;
}

export default function StripeCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [error, setError] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [financeWritesEnabled, setFinanceWritesEnabled] = useState(false);

  // Finance-Status laden
  useEffect(() => {
    axios.get(`${API}/api/superadmin/finance/status`, { headers: authHeader() })
      .then(r => setFinanceWritesEnabled(r.data.writes_enabled || false))
      .catch(() => {});
  }, []);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [includeDemo, setIncludeDemo] = useState(false);
  const [hiddenCancelled, setHiddenCancelled] = useState(0);
  const [hiddenDemo, setHiddenDemo] = useState(0);

  const load = async (s = search, p = page, cancelled = includeCancelled, demo = includeDemo) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: p, limit: 20, include_cancelled: cancelled, include_demo: demo });
      if (s) params.set('search', s);
      const res = await axios.get(`${API}/api/superadmin/stripe-cc/customers?${params}`, { headers: authHeader() });
      setCustomers(res.data.customers || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setHiddenCancelled(res.data.hidden_cancelled || 0);
      setHiddenDemo(res.data.hidden_demo || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Fehler beim Laden');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(search, page, includeCancelled, includeDemo); }, [page, includeCancelled, includeDemo]); // eslint-disable-line

  const handleSearch = () => { setPage(1); setSearch(searchInput); load(searchInput, 1, includeCancelled, includeDemo); };

  // Stripe-Profil via bestehendem Endpoint laden
  const openProfile = async (cust) => {
    setSelectedCustomer(cust);
    setProfileData(null);
    setProfileLoading(true);
    try {
      const res = await axios.get(`${API}/api/superadmin/customers/${cust.id}/stripe-profile`, { headers: authHeader() });
      setProfileData(res.data);
    } catch (e) {
      setProfileData({ error: e?.response?.data?.detail || 'Fehler' });
    } finally { setProfileLoading(false); }
  };

  return (
    <div className="space-y-4">
      {/* Suchzeile */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Firmenname, E-Mail oder Kundennummer…"
            className="pl-9"
            data-testid="stripe-customers-search"
          />
        </div>
        <Button onClick={handleSearch} disabled={loading} className="gap-1.5" data-testid="stripe-customers-search-btn">
          <Search className="h-4 w-4" /> Suchen
        </Button>
        <Button variant="outline" onClick={() => load()} disabled={loading} className="gap-1.5">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Filter-Toggles */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs text-muted-foreground">{total} Kunden</span>
        {hiddenCancelled > 0 && (
          <button
            onClick={() => { setIncludeCancelled(v => !v); setPage(1); }}
            className={`text-xs px-2.5 py-1.5 rounded-lg border font-medium transition-all ${
              includeCancelled
                ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/20 dark:border-red-700 dark:text-red-400'
                : 'border-gray-200 dark:border-gray-700 text-muted-foreground hover:border-red-300'
            }`}
            data-testid="toggle-cancelled"
          >
            {includeCancelled ? '✓' : '+'} {hiddenCancelled} Gekündigte
          </button>
        )}
        {hiddenDemo > 0 && (
          <button
            onClick={() => { setIncludeDemo(v => !v); setPage(1); }}
            className={`text-xs px-2.5 py-1.5 rounded-lg border font-medium transition-all ${
              includeDemo
                ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-400'
                : 'border-gray-200 dark:border-gray-700 text-muted-foreground hover:border-blue-300'
            }`}
            data-testid="toggle-demo"
          >
            {includeDemo ? '✓' : '+'} {hiddenDemo} Demo
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 rounded-xl text-sm text-red-700">
          <XCircle className="h-4 w-4 flex-shrink-0" /> {error}
        </div>
      )}

      {/* Kundenliste */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="space-y-2">
          {customers.map(c => (
            <div
              key={c.id}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors"
              onClick={() => openProfile(c)}
              data-testid={`stripe-customer-row-${c.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                    <Users className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold truncate">{c.company_name || '–'}</p>
                      {c.subscription_status && (
                        <StatusBadge status={c.subscription_status} statusDe={c.subscription_status_de} />
                      )}
                      {(c.customer_number || '').startsWith('DEMO') && (
                        <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 rounded-full font-medium">Demo</span>
                      )}
                      {c.status === 'canceled' && !c.subscription_status && (
                        <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 dark:bg-gray-800 rounded-full font-medium">Inaktiv</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap mt-0.5">
                      <span className="text-xs text-muted-foreground">{c.email || '–'}</span>
                      {c.customer_number && <span className="text-xs text-muted-foreground">#{c.customer_number}</span>}
                      <span className="text-xs text-muted-foreground">{c.location_count || 0} Standort{c.location_count !== 1 ? 'e' : ''}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {c.has_stripe ? (
                    c.has_payment_method
                      ? <CreditCard className="h-4 w-4 text-green-500" title="Zahlungsmethode vorhanden" />
                      : <AlertTriangle className="h-4 w-4 text-amber-500" title="Keine Zahlungsmethode" />
                  ) : (
                    <span className="text-xs text-muted-foreground px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">Kein Stripe</span>
                  )}
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            </div>
          ))}
          {customers.length === 0 && !loading && (
            <p className="text-center py-12 text-muted-foreground text-sm">Keine Kunden gefunden</p>
          )}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Zurück</Button>
          <span className="text-sm text-muted-foreground">Seite {page} von {pages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}>Weiter</Button>
        </div>
      )}

      {/* Kunden-Profil Modal (nutzt bestehenden Endpoint) */}
      {selectedCustomer && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setSelectedCustomer(null)}>
          <div className="bg-white dark:bg-gray-900 w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white dark:bg-gray-900 px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <div>
                <p className="text-base font-bold">{selectedCustomer.company_name}</p>
                <p className="text-xs text-muted-foreground">{selectedCustomer.email}</p>
              </div>
              <button onClick={() => setSelectedCustomer(null)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-muted-foreground">✕</button>
            </div>
            <div className="px-5 py-4 space-y-4">
              {profileLoading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
              {profileData?.error && <p className="text-sm text-red-600">{profileData.error}</p>}
              {profileData && !profileData.error && (
                <>
                  {/* Abonnement */}
                  {profileData.subscription && (
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Abonnement</p>
                      <div className="bg-gray-50 dark:bg-gray-800/40 rounded-xl p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">{profileData.subscription.plan_name}</span>
                          <StatusBadge status={profileData.subscription.status} statusDe={profileData.subscription.status_de} />
                        </div>
                        <div className="text-sm font-semibold">{profileData.subscription.plan_amount_eur?.toFixed(2)} €/Monat</div>
                        {profileData.subscription.current_period_end && (
                          <p className="text-xs text-muted-foreground">Nächste Zahlung: {profileData.subscription.current_period_end}</p>
                        )}
                        {profileData.subscription.cancel_at_period_end && (
                          <p className="text-xs text-amber-600 font-medium">Gekündigt zum Laufzeitende</p>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Rechnungen */}
                  {profileData.invoices?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Letzte Rechnungen</p>
                      <div className="space-y-1.5">
                        {profileData.invoices.slice(0, 5).map(inv => (
                          <div key={inv.id} className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-800/40 rounded-lg">
                            <div>
                              <span className="text-sm">{inv.number || inv.id?.slice(-6)}</span>
                              <span className="text-xs text-muted-foreground ml-2">{inv.date}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{inv.amount_display}</span>
                              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                                inv.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                              }`}>{inv.status_de}</span>
                              {inv.pdf_url && (
                                <a href={inv.pdf_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline" onClick={e => e.stopPropagation()}>PDF</a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {!profileData.has_stripe && (
                    <p className="text-sm text-muted-foreground text-center py-4">Kein Stripe-Account für diesen Kunden</p>
                  )}

                  {/* Finance-Aktionen */}
                  {profileData.has_stripe && (
                    <StripeFinanceActions
                      customer={selectedCustomer}
                      invoices={profileData.invoices || []}
                      payments={profileData.recent_charges || []}
                      writesEnabled={financeWritesEnabled}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
